import { useEffect, useRef, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import MediaPicker from "@/components/admin/MediaPicker";

const inputCls = "w-full border border-lp-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-lp-cyan";

export default function HomepageEditor() {
  const [c, setC] = useState(null);
  const [saved, setSaved] = useState(false);
  const [picker, setPicker] = useState(null);
  const pickTarget = useRef(null);

  useEffect(() => { api.get("/site-content").then((r) => setC(r.data || {})).catch(() => setC({})); }, []);
  if (!c) return <div className="text-lp-muted">Loading…</div>;

  const set = (path, val) => {
    setC((prev) => {
      const next = structuredClone(prev);
      let o = next;
      for (let i = 0; i < path.length - 1; i++) { o[path[i]] = o[path[i]] ?? {}; o = o[path[i]]; }
      o[path[path.length - 1]] = val;
      return next;
    });
  };
  const g = (path, def = "") => path.reduce((o, k) => (o ? o[k] : undefined), c) ?? def;

  const save = async () => {
    await api.put("/admin/site-content", c);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const Section = ({ title, children }) => (
    <div className="bg-white border border-lp-border rounded-sm p-6 mb-5">
      <h3 className="font-heading font-700 text-lp-blue mb-4">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
  const Field = ({ label, path, area }) => (
    <div>
      <label className="text-xs font-600 text-lp-ink">{label}</label>
      {area ? <textarea data-testid={`homepage-${path.join("-")}`} rows={2} value={g(path)} onChange={(e) => set(path, e.target.value)} className={inputCls + " mt-1"} />
        : <input data-testid={`homepage-${path.join("-")}`} value={g(path)} onChange={(e) => set(path, e.target.value)} className={inputCls + " mt-1"} />}
    </div>
  );
  const ImageField = ({ label, path }) => (
    <div>
      <label className="text-xs font-600 text-lp-ink">{label}</label>
      <div className="flex items-center gap-3 mt-1">
        {g(path) && <img src={mediaUrl(g(path))} alt="" className="h-12 w-12 object-cover rounded-sm border border-lp-border" />}
        <input value={g(path)} onChange={(e) => set(path, e.target.value)} className={inputCls} />
        <button type="button" onClick={() => { pickTarget.current = path; setPicker(true); }} className="shrink-0 border border-lp-blue text-lp-blue font-600 px-3 py-2 rounded-sm text-sm">Library</button>
      </div>
    </div>
  );

  const stats = g(["stats"], []);
  const titleLines = g(["hero", "title_lines"], []);
  const why = g(["why"], []);
  const marquee = g(["marquee"], []);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-lp-grey py-2 z-10">
        <h1 className="font-heading font-700 text-2xl text-lp-blue">Homepage Content</h1>
        <button onClick={save} data-testid="homepage-save" className="inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-5 py-2.5 rounded-sm">
          <Save size={16} /> {saved ? "Saved ✓" : "Save Changes"}
        </button>
      </div>

      <Section title="SEO">
        <Field label="Page Title" path={["seo", "title"]} />
        <Field label="Meta Description" path={["seo", "description"]} area />
      </Section>

      <Section title="Hero">
        <Field label="Eyebrow" path={["hero", "eyebrow"]} />
        <div>
          <label className="text-xs font-600 text-lp-ink">Title Lines</label>
          {titleLines.map((l, i) => (
            <div key={i} className="flex gap-2 mt-1">
              <input value={l} onChange={(e) => { const n = [...titleLines]; n[i] = e.target.value; set(["hero", "title_lines"], n); }} className={inputCls} />
              <button onClick={() => set(["hero", "title_lines"], titleLines.filter((_, j) => j !== i))} className="text-destructive"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => set(["hero", "title_lines"], [...titleLines, ""])} className="mt-2 text-sm text-lp-cyan font-600 inline-flex items-center gap-1"><Plus size={14} /> Add line</button>
        </div>
        <Field label="Highlighted Word" path={["hero", "highlight"]} />
        <Field label="Subtitle" path={["hero", "subtitle"]} area />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Primary CTA Label" path={["hero", "cta_primary", "label"]} />
          <Field label="Primary CTA Link" path={["hero", "cta_primary", "link"]} />
          <Field label="Secondary CTA Label" path={["hero", "cta_secondary", "label"]} />
          <Field label="Secondary CTA Link" path={["hero", "cta_secondary", "link"]} />
          <Field label="Badge Value" path={["hero", "badge_value"]} />
          <Field label="Badge Label" path={["hero", "badge_label"]} />
        </div>
        <ImageField label="Hero Image" path={["hero", "image"]} />
      </Section>

      <Section title="Stats Row">
        {stats.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input value={s.value} onChange={(e) => { const n = [...stats]; n[i] = { ...n[i], value: e.target.value }; set(["stats"], n); }} placeholder="Value" className={inputCls} />
            <input value={s.label} onChange={(e) => { const n = [...stats]; n[i] = { ...n[i], label: e.target.value }; set(["stats"], n); }} placeholder="Label" className={inputCls} />
            <button onClick={() => set(["stats"], stats.filter((_, j) => j !== i))} className="text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => set(["stats"], [...stats, { value: "", label: "" }])} className="text-sm text-lp-cyan font-600 inline-flex items-center gap-1"><Plus size={14} /> Add stat</button>
      </Section>

      <Section title="Marquee Keywords">
        <input value={marquee.join(", ")} onChange={(e) => set(["marquee"], e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className={inputCls} placeholder="comma separated" />
      </Section>

      <Section title="About Block">
        <Field label="Eyebrow" path={["about", "eyebrow"]} />
        <Field label="Heading" path={["about", "heading"]} />
        <Field label="Paragraph 1" path={["about", "body", 0]} area />
        <Field label="Paragraph 2" path={["about", "body", 1]} area />
        <ImageField label="About Image" path={["about", "image"]} />
        <div>
          <label className="text-xs font-600 text-lp-ink">Tiles (comma separated)</label>
          <input value={(g(["about", "tiles"], []) || []).join(", ")} onChange={(e) => set(["about", "tiles"], e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className={inputCls + " mt-1"} />
        </div>
      </Section>

      <Section title="Why Labpronics">
        {why.map((w, i) => (
          <div key={i} className="border border-lp-border rounded-sm p-3 space-y-2">
            <input value={w.title} onChange={(e) => { const n = [...why]; n[i] = { ...n[i], title: e.target.value }; set(["why"], n); }} placeholder="Title" className={inputCls} />
            <textarea rows={2} value={w.desc} onChange={(e) => { const n = [...why]; n[i] = { ...n[i], desc: e.target.value }; set(["why"], n); }} placeholder="Description" className={inputCls} />
            <button onClick={() => set(["why"], why.filter((_, j) => j !== i))} className="text-destructive text-sm inline-flex items-center gap-1"><Trash2 size={14} /> Remove</button>
          </div>
        ))}
        <button onClick={() => set(["why"], [...why, { title: "", desc: "" }])} className="text-sm text-lp-cyan font-600 inline-flex items-center gap-1"><Plus size={14} /> Add reason</button>
      </Section>

      <Section title="CTA Band">
        <Field label="Heading" path={["cta_band", "heading"]} />
        <Field label="Text" path={["cta_band", "text"]} area />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Button Label" path={["cta_band", "button", "label"]} />
          <Field label="Button Link" path={["cta_band", "button", "link"]} />
        </div>
      </Section>

      <Section title="Footer">
        <Field label="Description" path={["footer", "description"]} area />
        <Field label="Address" path={["footer", "address"]} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone" path={["footer", "phone"]} />
          <Field label="Email" path={["footer", "email"]} />
        </div>
      </Section>

      <div className="text-right pb-10">
        <button onClick={save} className="inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-6 py-3 rounded-sm"><Save size={16} /> {saved ? "Saved ✓" : "Save Changes"}</button>
      </div>

      <MediaPicker open={!!picker} onClose={() => setPicker(false)} onSelect={(url) => { if (pickTarget.current) set(pickTarget.current, url); }} />
    </div>
  );
}
